import { cloneDeep } from 'lodash'; 
import { toastr } from 'react-redux-toastr';
import {
    pendingTask, // The action key for modifying loading state
    begin, // The action value if a "long" running task begun
    end, // The action value if a "long" running task ended
} from 'react-redux-spinner';
import * as api from '../../services/api-endpoints';
import i18n from '../../../../i18n';
import { AdminSidePanelAction, RuleSetType } from '../../../Enums';
import { RESET_RULE_SET, UPDATE_RULE_SET, ACTION_LOADING } from './ruleSet.actions';
import { ADD_RULE_SET_TO_LIST, UPDATE_ADMIN_APP_DATA, DELETE_RULE_SET_IN_LIST } from '../appData';
import { LOADING_END, LOADING_START } from '../../../store/loading';
import { createCopyName } from '../../../auxilaries';
import { formatResponse } from '../../../utils';

/**
 * Refreshes the admin app-data list consumed by the request setup wizard
 * for the given ruleset type. Mirrors the per-type API surface used elsewhere.
 *
 * @param {import('redux').Dispatch} dispatch
 * @param {number} ruleSetType
 */
const refreshRuleSetAppData = async (dispatch, ruleSetType) => {
    if (ruleSetType === RuleSetType.AwardingRule) {
        const awardingRulesList = await api.GetAwardingRulesList();
        dispatch({
            type: UPDATE_ADMIN_APP_DATA,
            payload: {
                awardingRulesList: formatResponse(awardingRulesList.data, 'id', false),
            },
        });
    }
    else if (ruleSetType === RuleSetType.BreakRule) {
        const allRuleSets = await api.getAllRuleSet();
        dispatch({
            type: UPDATE_ADMIN_APP_DATA,
            payload: {
                breakRuleList: allRuleSets.data.filter(x => x.ruleSetType === RuleSetType.BreakRule && x.isActive).map(x => ({ id: x.ruleSetId, label: x.name })),
            },
        });
    }
    else {
        const breRuleSetList = await api.getBreRuleSets();
        dispatch({
            type: UPDATE_ADMIN_APP_DATA,
            payload: {
                breRuleSetList: formatResponse(breRuleSetList.data, 'id', true),
            },
        });
    }
};

/**
 * @param {RuleSet.IRuleSetDetails} ruleSet
 * @param {(() => void)} callBack
 * @param {boolean} isDuplicating
 */
export const createRuleSet = (ruleSet, callBack, isDuplicating) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        dispatch({ type: ACTION_LOADING, payload: true });

        const request = cloneDeep(ruleSet);
        if (request.validationRuleSet && request.validationRuleSet.isValidatePaycodeOverlap) {
            request.validationRuleSet.paycodeOverlapList = request.validationRuleSet.overlapPaycodeListToValidate.included.map(x => x.id);
        }
        if (request.validationRuleSet && request.validationRuleSet.isValidateSameDayPaycode) {
            request.validationRuleSet.sameDayPaycodeList = request.validationRuleSet.sameDayPaycodeListToValidate.included.map(x => x.id);
        }
        if (request.validationRuleSet) {
            // TODO - OT : This should not be in the store, this should be in the local state
            delete request.validationRuleSet.overlapPaycodeListToValidate;
            delete request.validationRuleSet.sameDayPaycodeListToValidate;
        }
        const response = await api.createRuleSet(request);
        if (response.status === 200) {
            dispatch({
                type: ADD_RULE_SET_TO_LIST,
                payload: {
                    ...response.data,
                    requestType: ruleSet.ruleSetType === RuleSetType.AwardingRule ? null :  ruleSet.requestType,
                },
            });
            toastr.success(
                isDuplicating ? i18n.t('employmentBillboard:settings.ruleSet.duplication') : i18n.t('employmentBillboard:settings.ruleSet.creation'),
                isDuplicating ? i18n.t('employmentBillboard:settings.ruleSet.duplicationSuccess') : i18n.t('employmentBillboard:settings.ruleSet.creationSuccess'),
            );
            dispatch({ type: RESET_RULE_SET });

            await refreshRuleSetAppData(dispatch, ruleSet.ruleSetType);

            callBack();
        } else {
            toastr.error(
                isDuplicating ? i18n.t('employmentBillboard:settings.ruleSet.duplication') : i18n.t('employmentBillboard:settings.ruleSet.creation'),
                isDuplicating ? i18n.t('employmentBillboard:settings.ruleSet.duplicationFail') : i18n.t('employmentBillboard:settings.ruleSet.creationFail'),
            );
        }
    } catch (error) {
        toastr.error(
            isDuplicating ? i18n.t('employmentBillboard:settings.ruleSet.duplication') : i18n.t('employmentBillboard:settings.ruleSet.creation'),
            error.response?.data.Message,
        );
    } finally {
        dispatch({ type: ACTION_LOADING, payload: false });
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {number} ruleSetId
 * @param {AdminAppData.IPaycodeFormat[]} paycodeList
 * @param {(() => void)} callBack
 */
export const loadRuleSet = (ruleSetId, paycodeList) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.getRuleSet(ruleSetId);
        if (response.status === 200) {
            dispatch({
                type: RESET_RULE_SET
            });

            const validationRuleSet = response.data.validationRuleSet;
            const breakRules = response.data.breakRules;
            const paycodeOverlapList = validationRuleSet?.paycodeOverlapList || [];
            const sameDayPaycodeList = validationRuleSet?.sameDayPaycodeList || [];
            dispatch({
                type: UPDATE_RULE_SET,
                payload: {
                    ...response.data,
                    validationRuleSet: validationRuleSet && {
                        ...validationRuleSet,
                        isTimeAfterShift: validationRuleSet.isTimeAfterShift || false,
                        maxHoursInRollingPeriodRules: validationRuleSet.maxHoursInRollingPeriodRules || [],
                        overlapPaycodeListToValidate: {
                            included: paycodeList.filter(x => paycodeOverlapList.includes(x.id)),
                            excluded: paycodeList.filter(x => !paycodeOverlapList.includes(x.id)),
                        },
                        sameDayPaycodeListToValidate: {
                            included: paycodeList.filter(x => sameDayPaycodeList.includes(x.id)),
                            excluded: paycodeList.filter(x => !sameDayPaycodeList.includes(x.id)),
                        }
                    },
                    breakRules: breakRules?.map((rule, index) => ({ ...rule, id: index })),
                    adminSidePanelAction: AdminSidePanelAction.Edit
                },
            });
        } else {
            toastr.error('Loading rule set', 'Loading failed');
        }
    } catch (error) {
        toastr.error('Loading rule set', error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {number} ruleSetId
 * @param {AdminAppData.IPaycodeFormat[]} paycodeList
 * @param {string[]} allRuleSetNames
 * @param {string} copyPrefix
 * @param {(() => void)} callBack
 */
export const loadDuplicateRuleSet = (ruleSetId, paycodeList, allRuleSetNames, copyPrefix) =>
/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });

            const response = await api.getRuleSet(ruleSetId);

            const validationRuleSet = response.data.validationRuleSet;
            const breakRules = response.data.breakRules;
            const paycodeOverlapList = validationRuleSet?.paycodeOverlapList || [];
            const sameDayPaycodeList = validationRuleSet?.sameDayPaycodeList || [];
            if (response.status === 200) {
                dispatch({
                    type: RESET_RULE_SET
                })
                dispatch({
                    type: UPDATE_RULE_SET,
                    payload: {
                        ...response.data,
                        name: createCopyName(
                            response.data.name,
                            allRuleSetNames,
                            copyPrefix),
                        adminSidePanelAction: AdminSidePanelAction.Duplicate,
                        validationRuleSet: validationRuleSet && {
                            ...validationRuleSet,
                            isTimeAfterShift: validationRuleSet.isTimeAfterShift || false,
                            maxHoursInRollingPeriodRules: validationRuleSet.maxHoursInRollingPeriodRules || [],
                            overlapPaycodeListToValidate: {
                                included: paycodeList.filter(x => paycodeOverlapList.includes(x.id)),
                                excluded: paycodeList.filter(x => !paycodeOverlapList.includes(x.id)),
                            },
                            sameDayPaycodeListToValidate: {
                                included: paycodeList.filter(x => sameDayPaycodeList.includes(x.id)),
                                excluded: paycodeList.filter(x => !sameDayPaycodeList.includes(x.id)),
                            }
                        },
                        breakRules: breakRules?.map((rule, index) => ({ ...rule, id: index })),
                        isEditable: true,
                    },
                });
            } else {
                toastr.error('Loading rule set', 'Loading failed');
            }
        } catch (error) {
            toastr.error('Loading rule set', error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {RuleSet.IRuleSetDetails} ruleSet
 * @param {{ruleSets: RuleSet.IRuleSetBrief[]}} storeData
 * @param {(() => void)} callBack
 */
export const editRuleSet = (ruleSet, storeData, callBack) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { ruleSets } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        dispatch({ type: ACTION_LOADING, payload: true });

        const request = cloneDeep(ruleSet);
        if (request.validationRuleSet && request.validationRuleSet.isValidatePaycodeOverlap) {
            request.validationRuleSet.paycodeOverlapList = request.validationRuleSet.overlapPaycodeListToValidate.included.map(x => x.id);
        }
        if (request.validationRuleSet && request.validationRuleSet.isValidateSameDayPaycode) {
            request.validationRuleSet.sameDayPaycodeList = request.validationRuleSet.sameDayPaycodeListToValidate.included.map(x => x.id);
        }

        if (request.validationRuleSet) {
            // TODO - OT : This should not be in the store, this should be in the local state
            delete request.validationRuleSet.overlapPaycodeListToValidate;
            delete request.validationRuleSet.sameDayPaycodeListToValidate;
        }

        const response = await api.updateRuleSet(ruleSet.ruleSetId, request);
        if (response.status === 200) {
            const updatedAwardingRuleSet = response.data;
            const indexToUpdate = ruleSets.findIndex((x) => x.ruleSetId === updatedAwardingRuleSet.ruleSetId);
            ruleSets[indexToUpdate] = {
                ...updatedAwardingRuleSet,
            };

            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: { ruleSets },
            });

            await refreshRuleSetAppData(dispatch, ruleSet.ruleSetType);

            toastr.success(i18n.t('employmentBillboard:settings.ruleSet.edition'), i18n.t('employmentBillboard:settings.ruleSet.editionSuccess'));
            dispatch({ type: RESET_RULE_SET });
            callBack();
        } else {
            toastr.error(i18n.t('employmentBillboard:settings.ruleSet.edition'), i18n.t('employmentBillboard:settings.ruleSet.editionFail'));
        }
    } catch (error) {
        toastr.error(i18n.t('employmentBillboard:settings.ruleSet.edition'), error.response.data.Message);
    } finally {
        dispatch({ type: ACTION_LOADING, payload: false });
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {number} ruleSetId
 * @param {number} ruleSetType
 */
export const deleteRuleSet = (ruleSetId, ruleSetType) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.deleteRuleSet(ruleSetId);
        if (response.status === 200) {
            dispatch({
                type: DELETE_RULE_SET_IN_LIST,
                payload: { ruleSetId },
            });

            await refreshRuleSetAppData(dispatch, ruleSetType);

            toastr.success(i18n.t('employmentBillboard:settings.ruleSet.deletion'), i18n.t('employmentBillboard:settings.ruleSet.deletionSuccess'));
        } else {
            toastr.success(i18n.t('employmentBillboard:settings.ruleSet.deletion'), i18n.t('employmentBillboard:settings.ruleSet.deletionFail'));
        }
    } catch (error) {
        toastr.error(i18n.t('employmentBillboard:settings.ruleSet.deletion'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
